import { Component } from '@theme/component';

/**
 * Announcement banner custom element that fades between messages on mobile.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #touchStartX = 0;

  #mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 749px)') : null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#mq?.addEventListener('change', this.#syncMode);
    this.#syncMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#mq?.removeEventListener('change', this.#syncMode);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !this.#isMobile()) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden || !this.#isMobile()) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || !this.#isMobile()) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #isMobile() {
    return Boolean(this.#mq?.matches);
  }

  #syncMode = () => {
    const slides = this.refs.slides ?? [];

    if (this.#isMobile() && slides.length > 1) {
      this.current = this.#current;
      this.play();
      return;
    }

    this.suspend();
    slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
  };

  #onTouchStart = (event) => {
    if (!this.#isMobile()) return;
    this.#touchStartX = event.changedTouches[0]?.screenX || 0;
    this.suspend();
  };

  #onTouchEnd = (event) => {
    if (!this.#isMobile()) return;

    const dx = (event.changedTouches[0]?.screenX || 0) - this.#touchStartX;
    if (Math.abs(dx) > 40) {
      if (dx < 0) this.next();
      else this.previous();
    }

    this.resume();
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
      return;
    }

    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
